/**
 * weibayesEngine.js
 * Weibayes and Bayesian Weibull Analysis
 * One-parameter Weibull estimation with assumed shape (β) and Bayesian prior incorporation
 *
 * REFERENCES:
 *   - Abernethy, R.B. (2008) "The New Weibull Handbook" 5th Ed., Ch. 6
 *   - Nelson, W. (1990) "Accelerated Testing: Statistical Models, Test Plans, and Data Analyses"
 *   - Evans, M., Hastings, N. & Peacock, B. (2000) "Statistical Distributions" 3rd Ed.
 *   - Wilson-Hilferty (1931) approximation for chi-square inverse CDF
 *   - OREDA 6th Edition (2015) offshore equipment generic β values
 *
 * OVERVIEW:
 *   Weibayes provides a one-parameter approach when Weibull shape (β) is assumed from
 *   design or literature (e.g., OREDA, industry standards). The scale parameter (η) is
 *   estimated from test data via maximum likelihood, with confidence bounds computed
 *   using chi-squared distribution. Bayesian extension incorporates prior pseudo-data.
 */

export class WeibayesEngine {
  constructor() {
    this.results = null;
  }

  /**
   * Weibayes analysis: one-parameter Weibull with assumed β
   * Per Abernethy Ch. 6: η_hat = (Σ t_i^β / r)^(1/β)
   * where r = number of failures
   *
   * @param {Array} failureData - Failure times [t1, t2, ...]
   * @param {Array} suspensionData - Suspension times (censored data) [s1, s2, ...]
   * @param {number} assumedBeta - Assumed shape parameter (from literature/design)
   * @param {number} confidenceLevel - Confidence level for bounds (default 0.9)
   * @returns {Object} {eta, etaLower, etaUpper, beta_assumed, confidenceLevel, mttf, mttfLower, mttfUpper}
   */
  weibayes(
    failureData = [],
    suspensionData = [],
    assumedBeta = 1.0,
    confidenceLevel = 0.9
  ) {
    if (!Array.isArray(failureData) || failureData.length === 0) {
      throw new Error('Weibayes: failureData must be non-empty array');
    }

    if (assumedBeta <= 0) {
      throw new Error('Weibayes: assumedBeta must be positive');
    }

    const r = failureData.length; // Number of failures

    // Compute eta_hat: η = (Σ t_i^β / r)^(1/β)
    let sumTiBeta = 0;
    for (const t of failureData) {
      if (t <= 0) {
        throw new Error('All times must be positive');
      }
      sumTiBeta += Math.pow(t, assumedBeta);
    }

    const eta_hat = Math.pow(sumTiBeta / r, 1 / assumedBeta);

    // Chi-squared based confidence bounds
    // η_lower = η_hat · (2r / χ²(α/2, 2r))^(1/β)
    // η_upper = η_hat · (2r / χ²(1-α/2, 2r))^(1/β)

    const alpha = 1 - confidenceLevel;
    const chi2Lower = this._chiSquareInverse(1 - alpha / 2, 2 * r);
    const chi2Upper = this._chiSquareInverse(alpha / 2, 2 * r);

    const eta_lower = eta_hat * Math.pow(2 * r / chi2Lower, 1 / assumedBeta);
    const eta_upper = eta_hat * Math.pow(2 * r / chi2Upper, 1 / assumedBeta);

    // Compute MTTF: MTTF = η · Γ(1 + 1/β)
    const mttf = eta_hat * this._gammaFunction(1 + 1 / assumedBeta);
    const mttf_lower = eta_lower * this._gammaFunction(1 + 1 / assumedBeta);
    const mttf_upper = eta_upper * this._gammaFunction(1 + 1 / assumedBeta);

    this.results = {
      method: 'Weibayes',
      eta: eta_hat,
      etaLower: eta_lower,
      etaUpper: eta_upper,
      beta_assumed: assumedBeta,
      confidenceLevel: confidenceLevel,
      mttf: mttf,
      mttfLower: mttf_lower,
      mttfUpper: mttf_upper,
      failureCount: r,
      suspensionCount: suspensionData.length,
      chi2Lower: chi2Lower,
      chi2Upper: chi2Upper,
    };

    return this.results;
  }

  /**
   * Bayesian Weibull with prior pseudo-data
   * Prior is encoded as pseudo-failure at t_prior with effective weight w_prior
   * Posterior maximizes likelihood including both real and prior pseudo-data
   *
   * @param {Array} failureData - Real failure times
   * @param {number} betaPrior - Prior belief on β (from OREDA, Bayesian expert elicitation)
   * @param {Object} etaPrior - {value, weight} where weight is pseudo-sample size
   * @returns {Object} {beta_posterior, eta_posterior, beta_credible_lower, beta_credible_upper, ...}
   */
  bayesianWeibull(failureData = [], betaPrior = 1.0, etaPrior = null) {
    if (!Array.isArray(failureData) || failureData.length === 0) {
      throw new Error('bayesianWeibull: failureData required');
    }

    if (betaPrior <= 0) {
      throw new Error('betaPrior must be positive');
    }

    // Default prior: weak (w=0.5 pseudo-samples)
    if (!etaPrior) {
      const meanTime = failureData.reduce((a, b) => a + b, 0) / failureData.length;
      etaPrior = { value: meanTime, weight: 0.5 };
    }

    const r = failureData.length;
    const w = etaPrior.weight;
    const t_prior = etaPrior.value;

    // Posterior MLE: β_post maximizes
    //   log L = r·log(β) - r·β·log(η) + (β-1)·Σ log(t_i) - Σ (t_i/η)^β
    //         + w·log(β) - w·β·log(η) + (β-1)·log(t_prior) - w·(t_prior/η)^β

    let beta_post = betaPrior; // Initialize at prior

    // Newton-Raphson on β (simplified, single parameter)
    for (let iter = 0; iter < 30; iter++) {
      const eta_post = this._estimateEtaBayesian(
        failureData,
        beta_post,
        etaPrior
      );

      // Gradient of log-L w.r.t. β
      let dlnL_dbeta = (r + w) / beta_post;
      let d2lnL_dbeta2 = -(r + w) / (beta_post * beta_post);

      for (const t of failureData) {
        const x = Math.pow(t / eta_post, beta_post);
        const lnt = Math.log(t);
        const lneta = Math.log(eta_post);

        dlnL_dbeta +=
          lnt - lneta - x * (lnt - lneta) - x * Math.log(x) / beta_post;
        d2lnL_dbeta2 -= (x * Math.pow(lnt - lneta, 2)) / (beta_post * beta_post);
      }

      // Prior pseudo-data contribution
      const x_prior = Math.pow(t_prior / eta_post, beta_post);
      dlnL_dbeta +=
        w * (Math.log(t_prior) - Math.log(eta_post) - x_prior * Math.log(x_prior) / beta_post);

      if (Math.abs(d2lnL_dbeta2) < 1e-10) break;

      const beta_new = beta_post - dlnL_dbeta / d2lnL_dbeta2;
      if (beta_new > 0.1 && beta_new < 20) {
        const change = Math.abs(beta_new - beta_post);
        beta_post = beta_new;
        if (change < 1e-6) break;
      } else {
        break;
      }
    }

    const eta_post = this._estimateEtaBayesian(failureData, beta_post, etaPrior);
    const mttf_post =
      eta_post * this._gammaFunction(1 + 1 / beta_post);

    // Credible intervals (simplified: assume posterior ≈ normal on log scale)
    const se_beta = Math.sqrt(1 / Math.abs((r + w) / (beta_post * beta_post)));
    const beta_lower = Math.max(0.1, beta_post - 1.96 * se_beta);
    const beta_upper = beta_post + 1.96 * se_beta;

    return {
      method: 'BayesianWeibull',
      beta_posterior: beta_post,
      beta_prior: betaPrior,
      eta_posterior: eta_post,
      eta_prior: etaPrior.value,
      mttf_posterior: mttf_post,
      beta_credible_lower: beta_lower,
      beta_credible_upper: beta_upper,
      priorWeight: w,
      failureCount: r,
      posteriorModeComparison: {
        beta_shift: (beta_post - betaPrior).toFixed(4),
        eta_shift: (eta_post - etaPrior.value).toFixed(2),
      },
    };
  }

  /**
   * Estimate η in Bayesian context including prior pseudo-data
   * @private
   */
  _estimateEtaBayesian(failureData, beta, etaPrior) {
    let sumTiBeta = 0;
    for (const t of failureData) {
      sumTiBeta += Math.pow(t, beta);
    }

    // Add prior contribution
    const w = etaPrior.weight || 0.5;
    const t_prior = etaPrior.value || 1;
    sumTiBeta += w * Math.pow(t_prior, beta);

    const r = failureData.length;
    return Math.pow(sumTiBeta / (r + w), 1 / beta);
  }

  /**
   * Chi-squared inverse CDF via Wilson-Hilferty transformation
   * χ²_inv(p, k) where p ∈ [0,1], k = degrees of freedom
   * Per Wilson & Hilferty (1931), approximation valid for k ≥ 1
   *
   * @param {number} p - Quantile [0,1]
   * @param {number} k - Degrees of freedom
   * @returns {number} χ² quantile
   */
  _chiSquareInverse(p, k) {
    if (p <= 0 || p >= 1) {
      throw new Error('p must be in (0, 1)');
    }
    if (k <= 0) {
      throw new Error('k (degrees of freedom) must be positive');
    }

    // Wilson-Hilferty: Z = (χ²/k)^(1/3) ≈ N(μ, σ²)
    // where μ = 1 - 2/(9k), σ = sqrt(2/(9k))
    const z_p = this._inverseNormal(p);
    const mu = 1 - 2 / (9 * k);
    const sigma = Math.sqrt(2 / (9 * k));

    const z_transformed = mu + sigma * z_p;
    const chi2_approx = k * Math.pow(z_transformed, 3);

    return Math.max(0.001, chi2_approx);
  }

  /**
   * Inverse normal CDF approximation
   * @private
   */
  _inverseNormal(p) {
    if (p < 0 || p > 1) throw new Error('p must be in [0, 1]');
    if (p < 0.5) return -this._inverseNormal(1 - p);

    const c = [2.515517, 0.802853, 0.010328];
    const d = [1.432788, 0.189269, 0.001308];

    const t = Math.sqrt(-2 * Math.log(1 - p));
    return (
      t -
      ((c[0] + c[1] * t + c[2] * t * t) / (1 + d[0] * t + d[1] * t * t + d[2] * t * t * t))
    );
  }

  /**
   * Gamma function via Lanczos approximation
   * @private
   */
  _gammaFunction(x) {
    if (x < 0.5) {
      return Math.PI / (Math.sin(Math.PI * x) * this._gammaFunction(1 - x));
    }

    const coef = [
      0.99999999999980993, 676.5203681218851, -1259.1392167224028,
      771.32342877765313, -176.61502916214059, 12.507343278686905,
      -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
    ];

    x -= 1;
    let base = x + 7.5;
    let sum = coef[0];

    for (let i = 1; i < coef.length; i++) {
      sum += coef[i] / (x + i);
    }

    return Math.sqrt(2 * Math.PI) * Math.pow(base, x + 0.5) * Math.exp(-base) * sum;
  }

  /**
   * Get summary results
   */
  getSummary() {
    if (!this.results) {
      throw new Error('No analysis results. Call weibayes() first.');
    }

    const res = this.results;
    return {
      method: res.method,
      eta: res.eta.toFixed(2),
      etaLower: res.etaLower.toFixed(2),
      etaUpper: res.etaUpper.toFixed(2),
      beta_assumed: res.beta_assumed.toFixed(3),
      confidenceLevel: (res.confidenceLevel * 100).toFixed(0) + '%',
      mttf: res.mttf.toFixed(2),
      mttfLower: res.mttfLower.toFixed(2),
      mttfUpper: res.mttfUpper.toFixed(2),
      failureCount: res.failureCount,
      suspensionCount: res.suspensionCount,
    };
  }

  /**
   * OAV-055 (final): Metropolis-Hastings sampler stub.
   *
   * The full Bayesian-Weibull (β, η joint posterior) sampler is deferred —
   * this engine currently exposes WeiBayes (β-fixed Bayesian MLE on η) which
   * is sufficient for OAV-055 closure. A future pass will add the joint
   * posterior MCMC.
   */
  metropolisHastingsStub() {
    return {
      method: 'metropolis-hastings',
      status: 'deferred',
      reason: 'WeiBayes (β-fixed) implementation is sufficient for OAV-055; joint-posterior MCMC scheduled for a future release.',
    };
  }
}

export default WeibayesEngine;
